import { useQueryClient } from "@tanstack/react-query";
import type { ImmutableObject } from "@/lib/immutable";
import type { Store } from "../../hooks/store";
import { useStore } from "../../hooks/store";

export type FwQueryResponse<TResponse> = {
	Items: TResponse[];
	PageNo: number;
	PageSize: number;
	TotalItems: number;
	Sort: string;
};

export type FwQueryFilter = {
	Field: string;
	Op:
		| "="
		| "<>"
		| "contains"
		| "does not contain"
		| "in"
		| "not in"
		| "starts with"
		| "ends with"
		| ">"
		| ">="
		| "<"
		| "<";
	Value: string;
};

export const FWQUERYBROWSER_DEFAULT_PAGESIZE = 20;

export class FwQueryBrowserState<T> {
	data: FwQueryResponse<T> | undefined;
	pageNo: number = 1;
	pageSize: number = FWQUERYBROWSER_DEFAULT_PAGESIZE;
	sort: string | undefined;
	filters: FwQueryFilter[] = [];
	isLoading: boolean = false;
	allowFirst: boolean = false;
	allowPrev: boolean = false;
	allowNext: boolean = false;
	allowLast: boolean = false;
	allowSetPageNo: boolean = false;
	allowSetPageSize: boolean = false;
}

// Helper function to build query URL
function buildQueryUrl(
	baseUrl: string,
	pageNo: number,
	pageSize: number,
	sort: string | undefined,
	filters: FwQueryFilter[],
): string {
	const url = new URL(baseUrl, window.location.origin);
	url.searchParams.append("pageno", pageNo.toString());
	url.searchParams.append("pagesize", pageSize.toString());

	if (sort) {
		url.searchParams.append("sort", sort);
	}

	if (filters && filters.length > 0) {
		url.searchParams.append("filter", JSON.stringify(filters));
	}

	return url.toString();
}

// Helper function to fetch data with authentication
async function fetchWithAuth<T>(url: string): Promise<FwQueryResponse<T>> {
	const headers: Record<string, string> = {};

	const response = await fetch(url, {
		headers,
		method: "GET",
	});

	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	}

	return response.json();
}

export class FwQueryBrowserController<T> {
	private state: Store<FwQueryBrowserState<T>>;
	private queryClient: ReturnType<typeof useQueryClient>;

	get(): ImmutableObject<FwQueryBrowserState<T>> {
		return this.state.getState();
	}

	getUrlAsync: () => Promise<string | undefined>;

	constructor(
		store: Store<FwQueryBrowserState<T>>,
		getUrlAsync: () => Promise<string | undefined>,
		queryClient: ReturnType<typeof useQueryClient>,
	) {
		this.state = store;
		this.getUrlAsync = getUrlAsync;
		this.queryClient = queryClient;
	}

	getMaxPageNo(): number {
		const data = this.state.getState().data;
		if (data) {
			return Math.ceil(
				(data.TotalItems ?? 0) /
					(data.PageSize ?? FWQUERYBROWSER_DEFAULT_PAGESIZE),
			);
		}
		return 1;
	}

	toggleLoading(isLoading: boolean): void {
		const newState: Partial<FwQueryBrowserState<T>> = {};
		newState.allowFirst = false;
		newState.allowPrev = false;
		newState.allowNext = false;
		newState.allowLast = false;
		newState.allowSetPageNo = false;
		newState.allowSetPageSize = false;

		// if it finished loading and there's data, then
		const currentState = this.state.getState();
		if (!isLoading && currentState.data) {
			const pageNoMax = this.getMaxPageNo();
			if ((currentState.data?.PageNo ?? 1) > 1) {
				newState.allowFirst = true;
				newState.allowPrev = true;
			}
			if ((currentState.data?.PageNo ?? 0) < pageNoMax) {
				newState.allowNext = true;
				newState.allowLast = true;
				newState.allowSetPageNo = true;
			}
			newState.allowSetPageSize = true;
		}

		newState.isLoading = isLoading;
		this.state.setState((prev) => ({ ...prev, ...newState }));
	}

	async clearAsync(): Promise<void> {
		const newState: Partial<FwQueryBrowserState<T>> = {};
		newState.allowFirst = false;
		newState.allowPrev = false;
		newState.allowNext = false;
		newState.allowLast = false;
		newState.allowSetPageNo = false;
		newState.allowSetPageSize = false;
		newState.data = undefined;
		newState.filters = undefined;
		newState.isLoading = false;
		newState.pageNo = 1;
		newState.pageSize = FWQUERYBROWSER_DEFAULT_PAGESIZE;
		this.state.setState((prev) => ({ ...prev, ...newState }));

		// Invalidate all queries for this base URL
		const baseUrl = await this.getUrlAsync();
		if (baseUrl) {
			this.queryClient.invalidateQueries({
				queryKey: ["fwQuery", baseUrl],
			});
		}
	}

	async loadAsync(): Promise<void> {
		if (!this.state.getState().isLoading) {
			try {
				this.toggleLoading(true);
				const unfilteredUrl = await this.getUrlAsync();
				if (unfilteredUrl) {
					const url = buildQueryUrl(
						unfilteredUrl,
						this.state.getState().pageNo,
						this.state.getState().pageSize,
						this.state.getState().sort,
						[...this.state.getState().filters],
					);

					// Use TanStack Query to fetch data with caching
					const queryKey = [
						"fwQuery",
						unfilteredUrl,
						this.state.getState().pageNo,
						this.state.getState().pageSize,
						this.state.getState().sort,
						JSON.stringify(this.state.getState().filters),
					];

					const data = await this.queryClient.fetchQuery({
						queryKey,
						queryFn: () => fetchWithAuth<T>(url),
						staleTime: 5 * 60 * 1000, // 5 minutes
					});

					const newState: Partial<FwQueryBrowserState<T>> = {};
					newState.data = data;
					if (newState.data) {
						newState.pageNo = newState.data.PageNo;
						newState.pageSize = newState.data.PageSize;
					}
					this.state.setState((prev) => ({ ...prev, ...newState }));
				}
			} finally {
				this.toggleLoading(false);
			}
		}
	}

	async firstPageAsync(): Promise<void> {
		if (this.state.getState().allowFirst) {
			if (this.state.getState().pageNo >= 1) {
				const newState: Partial<FwQueryBrowserState<T>> = {};
				newState.pageNo = 1;
				this.state.setState((prev) => ({ ...prev, ...newState }));
			}
			await this.loadAsync();
		}
	}

	async prevPageAsync(): Promise<void> {
		if (this.state.getState().allowPrev) {
			if (this.state.getState().data) {
				if (this.state.getState().pageNo > 1) {
					const newState: Partial<FwQueryBrowserState<T>> = {};
					newState.pageNo = this.state.getState().pageNo - 1;
					this.state.setState((prev) => ({ ...prev, ...newState }));
				}
				await this.loadAsync();
			}
		}
	}

	async nextPageAsync(): Promise<void> {
		if (this.state.getState().allowNext) {
			const newState: Partial<FwQueryBrowserState<T>> = {};
			if (this.state.getState().data) {
				const maxPageNo = this.getMaxPageNo();

				if (this.state.getState().pageNo > maxPageNo) {
					newState.pageNo = maxPageNo;
					this.state.setState((prev) => ({ ...prev, ...newState }));
					await this.loadAsync();
				} else if (
					this.state.getState().pageNo >= 1 &&
					this.state.getState().pageNo < maxPageNo
				) {
					newState.pageNo = this.state.getState().pageNo + 1;
					this.state.setState((prev) => ({ ...prev, ...newState }));
					await this.loadAsync();
				} else if (this.state.getState().pageNo < 1) {
					newState.pageNo = 1;
					this.state.setState((prev) => ({ ...prev, ...newState }));
					await this.loadAsync();
				}
			}
		}
	}

	async lastPageAsync(): Promise<void> {
		if (this.state.getState().allowLast) {
			if (this.state.getState().data) {
				const maxPageNo = this.getMaxPageNo();
				if (this.state.getState().pageNo <= maxPageNo) {
					const newState: Partial<FwQueryBrowserState<T>> = {};
					newState.pageNo = maxPageNo;
					this.state.setState((prev) => ({ ...prev, ...newState }));
					await this.loadAsync();
				}
			}
		}
	}

	async setPageNoAsync(
		pageNo: number,
		load: boolean,
		overrideAllowSetPageNo: boolean,
	): Promise<void> {
		if (this.state.getState().allowSetPageNo || overrideAllowSetPageNo) {
			if (pageNo === 1) {
				this.state.setState((prev) => ({ ...prev, pageNo }));
				if (load) {
					await this.loadAsync();
				}
			} else if (pageNo > 1 && this.state.getState().data) {
				const maxPageNo = this.getMaxPageNo();
				if (
					this.state.getState().pageNo >= 1 &&
					this.state.getState().pageNo <= maxPageNo
				) {
					this.state.setState((prev) => ({ ...prev, pageNo }));
					if (load) {
						await this.loadAsync();
					}
				}
			}
		}
	}

	async setPageSizeAsync(pageSize: number): Promise<void> {
		if (this.state.getState().allowSetPageSize) {
			this.state.setState((prev) => ({ ...prev, pageSize }));
			// Invalidate queries when page size changes
			const baseUrl = await this.getUrlAsync();
			if (baseUrl) {
				this.queryClient.invalidateQueries({
					queryKey: ["fwQuery", baseUrl],
				});
			}
		}
	}

	async setFiltersAsync(filters: FwQueryFilter[]): Promise<void> {
		this.state.setState((prev) => ({ ...prev, filters }));
		// Invalidate queries when filters change
		const baseUrl = await this.getUrlAsync();
		if (baseUrl) {
			this.queryClient.invalidateQueries({
				queryKey: ["fwQuery", baseUrl],
			});
		}
	}
}

export function useFwQueryBrowserState<T>(
	store: Store<FwQueryBrowserState<T>>,
	getUrlAsync: () => Promise<string | undefined>,
): FwQueryBrowserController<T> {
	const queryClient = useQueryClient();
	useStore(store);
	return new FwQueryBrowserController<T>(store, getUrlAsync, queryClient);
}
